import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { authApi } from '../utils/api';

const Activate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  // Optional post-activation destination passed through by the inviter
  // (e.g. ensure_portal_user(next_path="/proposal/<id>")). Forwarded to
  // /login as ?redirect=... so the user lands there after 2FA.
  const nextPath = searchParams.get('next');

  const [tokenStatus, setTokenStatus] = useState('checking'); // 'checking', 'valid', 'invalid'
  const [tokenData, setTokenData] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Form fields
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenStatus('invalid');
        setError('No invitation token provided.');
        return;
      }

      try {
        const result = await authApi.validateToken(token);

        if (result.valid) {
          setTokenStatus('valid');
          setTokenData(result);
        } else if (/already activated/i.test(result.detail || result.message || '')) {
          // Not an error from the user's point of view: their account is
          // DONE — any activation link lands here once activated. Skip the
          // interstitial entirely and put them on the login page, with a
          // note so the jump isn't disorienting.
          navigate('/login?info=already_activated', { replace: true });
          return;
        } else {
          setTokenStatus('invalid');
          setError(result.detail || 'Invalid or expired invitation link.');
        }
      } catch (err) {
        setTokenStatus('invalid');
        setError('Failed to validate invitation. Please try again.');
      }
    };

    validateToken();
  }, [token]);

  const formatPhone = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');

    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    setPhone(formatPhone(e.target.value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name.');
      return;
    }

    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }

    setIsLoading(true);

    try {
      const result = await authApi.setPassword(token, password, firstName, lastName, phoneDigits);

      if (result.success) {
        setSuccess(true);
        const loginPath = nextPath
          ? `/login?redirect=${encodeURIComponent(nextPath)}`
          : '/login';
        setTimeout(() => navigate(loginPath), 3000);
      } else {
        setError(result.detail || 'Failed to complete registration.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (tokenStatus === 'checking') {
    return (
      <div className="activate-container">
        <div className="activate-card">
          <Loader2 className="spinner-large" size={48} />
          <p>Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (tokenStatus === 'invalid') {
    return (
      <div className="activate-container">
        <div className="activate-card error-state">
          <XCircle size={48} className="error-icon" />
          <h2>Invalid Invitation</h2>
          <p>{error}</p>
          {/* A link's token is cleared the moment activation completes, so
              the MOST COMMON way to land here is re-clicking the original
              activation email after setting up — which means the right next
              step is usually signing in, not another invitation. */}
          <p className="help-text">
            Already set up your account? Activation links only work once — just sign in
            with your email address instead.
          </p>
          <button
            type="button"
            className="submit-button"
            style={{ marginTop: 12 }}
            onClick={() => navigate('/login')}
          >
            Go to Sign In
          </button>
          <p className="help-text" style={{ marginTop: 12 }}>
            If you haven't set up your account yet, this link may have expired — contact
            your administrator to receive a new invitation.
          </p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="activate-container">
        <div className="activate-card success-state">
          <CheckCircle size={48} className="success-icon" />
          <h2>Account Activated!</h2>
          <p>Your account has been set up successfully.</p>
          <p className="help-text">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="activate-container">
      <div className="activate-card">
        <div className="activate-header">
          <img src="/logo.svg" alt="Macula Networks" className="portal-logo" />
          <h1>Welcome to the Client Portal</h1>
          {tokenData?.customer_name && (
            <p className="customer-name">{tokenData.customer_name}</p>
          )}
          <p className="invite-email">{tokenData?.email}</p>
        </div>

        <p className="activate-info">
          Complete your account setup by entering your information below.
        </p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="activate-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
                required
                autoComplete="given-name"
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Doe"
                required
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              value={phone}
              onChange={handlePhoneChange}
              placeholder="(555) 555-5555"
              required
              autoComplete="tel"
            />
            <span className="field-hint">Used for verification codes</span>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={8}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            <span className="field-hint">Minimum 8 characters</span>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="password-input">
              <input
                type={showPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="new-password"
              />
            </div>
          </div>

          <button type="submit" className="submit-button" disabled={isLoading}>
            {isLoading ? <Loader2 className="spinner" size={20} /> : 'Complete Setup'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Activate;
